export default function AlbuquerqueTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Albuquerque Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Albuquerque travelers actually move helps us anticipate the passport urgencies that arise here. From Kirtland AFB deployment timelines to cross-border family travel to Mexico, Central New Mexico travel operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* ABQ Sunport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">ABQ — Albuquerque International Sunport</h3>
                <p className="text-gray-400 text-xs">South of downtown Albuquerque, 5 miles from city center</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              ABQ Sunport is New Mexico's primary commercial airport with nonstop service to major hubs including Dallas, Denver, Phoenix, Los Angeles, and Atlanta — connecting Albuquerque travelers to Europe, Asia, and Latin America. Kirtland AFB personnel and their families frequently fly through ABQ for PCS moves and TDY rotations, often with passport validity requirements that standard processing cannot meet.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> ABQ Sunport international connections are almost always via a hub city. Check your passport expiration and destination entry requirements before booking — many countries require six months validity beyond your return date.
              </p>
            </div>
          </div>

          {/* Kirtland AFB & Military */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shield-star-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Kirtland AFB — Military PCS &amp; TDY</h3>
                <p className="text-gray-400 text-xs">Air Force Nuclear Weapons Center, AFRL, Space Force units</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Kirtland Air Force Base is one of the most mission-critical installations in the Department of Defense, hosting nuclear security, directed energy, and space research missions. PCS orders often arrive with 30 days or less to report to overseas assignments. Dependent passports must be current, and security clearance holders face additional scrutiny with expired travel documents. We regularly process expedited passports for Kirtland families on compressed timelines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Dependent passports must have at least six months validity for most overseas PCS assignments. Check every family member's expiration as soon as orders arrive — not the week before your flight out of ABQ.
              </p>
            </div>
          </div>

          {/* Cross-Border & Mexico Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-road-map-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Cross-Border &amp; Mexico Travel</h3>
                <p className="text-gray-400 text-xs">Ciudad Juárez, Chihuahua, family reunions throughout northern Mexico</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Albuquerque's deep cultural and economic ties to Mexico create consistent passport demand. Families travel south for quinceañeras, weddings, holidays, and reunions — often booking travel 2–6 weeks out. The 270-mile proximity to the border means many families cross regularly, and an expired passport discovered before a major family event creates genuine panic. We regularly process expedited renewals and child passports for cross-border family travel.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Mexico requires a valid passport for all air travel and most land crossings. Passport cards work for land/sea but not air. Check which document you need before booking — we expedite both book and card applications.
              </p>
            </div>
          </div>

          {/* UNM & Study Abroad */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">UNM &amp; Study Abroad Seasons</h3>
                <p className="text-gray-400 text-xs">University of New Mexico, CNM, St. John's College Santa Fe</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              The University of New Mexico sends hundreds of students abroad annually through robust Latin American Studies, Spanish language immersion, and engineering exchange programs. Application deadlines typically fall in October and March, but many students do not think about their passport until after acceptance — leaving as little as 4–6 weeks before orientation. First-time applicants require an in-person acceptance clerk visit, adding an extra step to an already compressed timeline.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many UNM students) require an in-person acceptance clerk visit. We guide students through the exact location, forms, and timeline so they do not miss their program orientation in Madrid, Buenos Aires, or Mexico City.
              </p>
            </div>
          </div>
        </div>

        {/* Sandia Labs / Intel callout */}
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-microscope-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Sandia National Labs, Intel &amp; Tech Sector Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Albuquerque's technology and national security research sector generates frequent international travel. Sandia Labs researchers attend conferences, collaborative meetings, and field assignments worldwide — often on 2–4 weeks' notice. Intel Rio Rancho professionals travel to facilities in Asia and Europe for manufacturing integration projects. These are not vacation trips — they are mission-critical work with immovable deadlines that standard passport processing simply cannot meet.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}